<template>
  <page>
    <v-card>
      <v-card-title>
        <div>
          <div class="headline">Loyalty Listing</div>
        </div>
        <v-spacer></v-spacer>
        <v-flex xs6>
          <v-text-field append-icon="mdi-magnify" label="Search" single-line hide-details v-model="search"></v-text-field>
        </v-flex>
      </v-card-title>
      <v-flex xs12 class="mt-3">
        <message :messages="messages"></message>
      </v-flex>
      <v-data-table v-bind:headers="headers" v-bind:items="items" v-bind:search="search" :rows-per-page-items="itemsPerPage" v-bind:pagination.sync="pagination" item-key="groupId" :loading="loading" :no-data-text="noDataText" :filter="customSearch">
        <template slot="items" slot-scope="props">
          <td>{{ props.item.groupId }}</td>
          <td>{{ props.item.groupDesc }}</td>
          <td class="text-xs-left">{{ formatDate(props.item.effDate) }}</td>
          <td class="text-xs-left">{{ formatDate(props.item.expDate) }}</td>
          <td class="text-xs-left">{{ props.item.noPastTran }}</td>
          <td class="text-xs-left">{{ props.item.noPastDays }}</td>
          <td class="text-xs-left">{{ props.item.minTotPrem }}</td>
          <td class="text-xs-right">
            <table-action :rowData="props" review @review="doReview(props)" remove @remove="doPromptRemove(props.item)" :loading="loading"></table-action>
          </td>
        </template>
      </v-data-table>
      <v-card-text>
        <v-btn color="primary" dark class="mb-2" @click="doNew()">
          Add Loyalty
          <v-icon right>mdi-wallet-membership</v-icon>
        </v-btn>
      </v-card-text>
      <v-dialog v-model="addLoyaltyDialog" max-width="600px" persistent>
        <v-card>
          <v-card-title>
            <span class="headline">{{newLoyalty? 'Add Loyalty' : 'Update Loyalty'}}</span>
          </v-card-title>
          <v-card-text>
            <message :messages="messages"></message>
            <v-container grid-list-md>
              <v-layout row wrap>
                <v-flex xs6 v-if="newLoyalty">
                  <v-autocomplete :label="labels.groupId" v-model="loyaltyForm.groupId" :items="memberGroupList" item-text="label" item-value="value" :error-messages="errors.collect('groupId')" data-vv-name="groupId" :data-vv-as="labels.groupId" v-validate="'required'" :loading="groupIdLoading"></v-autocomplete>
                </v-flex>
                <v-flex xs6 v-else>
                  <v-subheader class="queryLabel">{{labels.groupIdQuery}}</v-subheader>
                  <div class="title">{{usrMemberGrp}}</div>
                </v-flex>
                <v-flex xs6></v-flex>
                <v-flex xs6 v-if="newLoyalty">
                  <cal-picker v-model="loyaltyForm.effDate" :label="labels.effDate" v-validate="'required'" :data-vv-as="labels.effDate" data-vv-name="effDate" :error-messages="errors.collect('effDate')"></cal-picker>
                </v-flex>
                <v-flex xs6 v-else>
                  <v-subheader class="queryLabel">{{labels.effDateQuery}}</v-subheader>
                  <div class="title">{{formatDate(loyaltyForm.effDate)}}</div>
                </v-flex>
                <v-flex xs6>
                  <cal-picker v-model="loyaltyForm.expDate" :label="labels.expDate" v-validate="expDateValidation" :min="this.loyaltyForm.effDate" :data-vv-as="labels.expDate" data-vv-name="expDate" :error-messages="errors.collect('expDate')"></cal-picker>
                </v-flex>
                <v-flex xs12>
                  <v-layout row wrap>
                    <v-flex xs6>
                      <v-text-field v-int-mask="{intScale: 9}" v-model="loyaltyForm.noPastTran" :label="labels.noPastTran" type="tel" :error-messages="errors.collect('noPastTran')" data-vv-name="noPastTran" :data-vv-as="labels.noPastTran" v-validate="'numeric|max_value:999999999|min_value:0'"></v-text-field>
                    </v-flex>
                    <v-flex xs6>
                      <v-text-field v-int-mask="{intScale: 9}" v-model="loyaltyForm.noPastDays" :label="labels.noPastDays" type="tel" :error-messages="errors.collect('noPastDays')" data-vv-name="noPastDays" :data-vv-as="labels.noPastDays" v-validate="'numeric|max_value:999999999|min_value:0'"></v-text-field>
                    </v-flex>
                  </v-layout>
                </v-flex>
                <v-flex xs6>
                  <v-text-field ref="minTotPrem" v-num-mask v-model="loyaltyForm.minTotPrem" :label="labels.minTotPrem" type="tel" :error-messages="errors.collect('minTotPrem')" data-vv-name="minTotPrem" :data-vv-as="labels.minTotPrem" v-validate="'mask_decimal:2|mask_min_value:0.00|mask_max_value:99999999.99'"></v-text-field>
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="red darken-1" flat @click.native="doClose">Cancel</v-btn>
            <v-btn color="primary" flat @click.native="doSaveLoyalty">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <confirm v-model="promptRemove" @ok="doRemove">
        Are you sure that you want to delete the selected Loyalty Setting?
        <div class="title">{{deleteLoyalty}}</div>
      </confirm>
    </v-card>
  </page>
</template>

<script>
import * as TableConst from '@/mixins/table'
import MessageMixin from '@/mixins/message'
import { expandURI } from '@/utils/ajax'
import { extractMenu } from '@/utils/extract'
import { formatDisplayDate } from '@/utils/date'
import { formatDisplayNumber } from '@/utils/number'

import clone from 'clone'

export default {
  data() {
    return {
      headers: [
        { text: 'Group ID', align: 'left', value: 'groupId', sortable: false },
        { text: 'Group Description', align: 'left', value: 'groupDesc', sortable: false },
        { text: 'Effective Date', align: 'left', value: 'effDate', sortable: false },
        { text: 'Expiry Date', align: 'left', value: 'expDate', sortable: false },
        { text: 'No. of Past Transactions', align: 'left', value: 'noPastTran', sortable: false },
        { text: 'Past No. of Days', align: 'left', value: 'noPastDays', sortable: false },
        { text: 'Minimum Total Premium', align: 'left', value: 'minTotPrem', sortable: false },
        TableConst.tableActionHeader
      ],
      labels: {
        groupId: 'Group ID*',
        groupIdQuery: 'Group ID',
        effDate: 'Effective Date*',
        effDateQuery: 'Effective Date',
        expDate: 'Expiry Date*',
        noPastTran: 'No. of Past Transactions',
        noPastDays: 'Past No. of Days',
        minTotPrem: 'Minimum Total Premium'
      },
      loyaltyForm: {
        groupId: undefined,
        effDate: undefined,
        expDate: undefined,
        noPastTran: 0,
        noPastDays: 0,
        minTotPrem: undefined
      },
      totalItems: 0,
      loading: false,
      menu: [],
      items: [],
      promptRemove: false,
      selectedLoyalty: null,
      deleteLoyalty: '',
      newLoyalty: false,
      addLoyaltyDialog: false,
      memberGroupList: []
    }
  },
  computed: {
    usrMemberGrp() {
      if (this.selectedLoyalty != null) {
        return this.selectedLoyalty.groupId + ' - ' + this.selectedLoyalty.groupDesc
      } else {
        return ''
      }
    },
    expDateValidation() {
      return 'required|start_enddate:' + this.loyaltyForm.effDate
    }
  },
  methods: {
    async doInit() {
      await this.queryLoyalties()
    },
    async queryLoyalties() {
      const apiURI = this.$store.state.promotionManagement.menu.loyalties
      if (apiURI) {
        try {
          this.loading = true
          const result = await this.$http.get(apiURI)
          if (result.data) {
            this.totalItems = result.data.totalElements
            const { data } = result.data
            if (data) {
              // Format the data here
              this.items = []
              data.map((loyalty) => {
                const convertLoyalty = {
                  groupId: loyalty.groupId,
                  groupDesc: loyalty.groupDesc,
                  effDate: loyalty.effDate,
                  expDate: loyalty.expDate,
                  noPastTran: loyalty.noPastTran,
                  noPastDays: loyalty.noPastDays,
                  minTotPrem: this.formatDisplayNumber(loyalty.minTotPrem)
                }
                this.items.push(convertLoyalty)
              })
            } else {
              this.items = []
            }
          }
          this.loading = false
        } catch (err) {
          throw err
        }
      } else {
        this.$snotify.error('Invalid API URL')
      }
    },
    async getLoyalty(item) {
      const apiURI = this.$store.state.promotionManagement.menu.loyaltyGet
      if (apiURI) {
        try {
          const expand = {
            groupId: item.groupId,
            effDate: formatDisplayDate(item.effDate)
          }
          const result = await this.$http.get(expandURI(apiURI, expand))
          const { data } = result.data
          if (data) {
            // Loyalty Init
            this.memberGroupList = data.memberGroupList

            this.loyaltyForm.groupId = data.loyalty.groupId
            this.loyaltyForm.effDate = data.loyalty.effDate
            this.loyaltyForm.expDate = data.loyalty.expDate
            this.loyaltyForm.noPastTran = data.loyalty.noPastTran
            this.loyaltyForm.noPastDays = data.loyalty.noPastDays
            this.loyaltyForm.minTotPrem = data.loyalty.minTotPrem

            this.menu = extractMenu(result.data._links)
          }
        } catch (error) {
          throw error
        }
      } else {
        this.$snotify.error('Invalid API URL')
      }
    },
    async getMemberGroupList() {
      const apiURI = this.$store.state.promotionManagement.menu.memberGroupList
      if (apiURI) {
        try {
          const result = await this.$http.get(apiURI)
          const { data } = result.data
          if (data) {
            this.memberGroupList = data
          }
        } catch (error) {
          throw error
        }
      } else {
        this.$snotify.error('Invalid API URL')
      }
    },
    async doNew() {
      this.resetMessages()
      this.resetForm()
      this.newLoyalty = true
      this.selectedLoyalty = null
      await this.getMemberGroupList()
      this.addLoyaltyDialog = true
    },
    doClose() {
      this.resetMessages()
      this.resetForm()
      this.addLoyaltyDialog = false
      this.newLoyalty = false
      this.selectedLoyalty = null
    },
    async doReview(data) {
      this.resetMessages()
      this.resetForm()
      this.newLoyalty = false
      if (data && data.item && data.item.groupId) {
        this.selectedLoyalty = data.item
        await this.getMemberGroupList()
        await this.getLoyalty(data.item)
        this.addLoyaltyDialog = true
      }
    },
    doPromptRemove(data) {
      if (data) {
        this.promptRemove = true
        this.selectedLoyalty = data
        this.deleteLoyalty = data.groupId + ' - ' + data.groupDesc
      }
    },
    async doRemove() {
      this.resetMessages()
      const apiURI = this.$store.state.promotionManagement.menu.loyaltyDelete
      if (apiURI) {
        this.loading = true
        const expand = {
          groupId: this.selectedLoyalty.groupId,
          effDate: this.selectedLoyalty.effDate
        }
        try {
          const result = await this.$http.post(apiURI, expand)
          this.notify(result.data)
          // Reinit
          this.queryLoyalties()
          this.resetForm()
        } catch (err) {
          console.log(err)
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.$snotify.error('Invalid API URL')
      }
    },
    doSaveLoyalty() {
      this.resetMessages()
      this.$validator.validateAll().then(result => {
        if (result) {
          if (this.newLoyalty) {
            this.doCreateLoyalty()
          } else {
            this.doUpdateLoyalty()
          }
        } else {
          this.promptFieldCheck()
        }
      })
    },
    async doCreateLoyalty() {
      const apiURI = this.$store.state.promotionManagement.menu.loyaltyCreate
      if (apiURI) {
        this.submitting = true
        try {
          this.$refs.minTotPrem.$el.cleave.setRawValue(this.loyaltyForm.minTotPrem)
          const unMaskMinTotPrem = this.$refs.minTotPrem.$el.cleave.getRawValue()
          let massageForm = clone(this.loyaltyForm)
          if (massageForm.minTotPrem) {
            massageForm.minTotPrem = unMaskMinTotPrem
          }
          const result = await this.$http.post(apiURI, massageForm)
          const { data } = result
          if (data) {
            this.notify(data)
            this.queryLoyalties()
            this.resetForm()
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
          this.submitting = false
        }
      } else {
        this.$snotify.error('Invalid API URL')
      }
    },
    async doUpdateLoyalty() {
      const apiURI = this.menu.update
      if (apiURI) {
        this.submitting = true
        try {
          this.$refs.minTotPrem.$el.cleave.setRawValue(this.loyaltyForm.minTotPrem)
          const unMaskMinTotPrem = this.$refs.minTotPrem.$el.cleave.getRawValue()
          let massageForm = clone(this.loyaltyForm)
          if (massageForm.minTotPrem) {
            massageForm.minTotPrem = unMaskMinTotPrem
          }
          const result = await this.$http.put(apiURI, massageForm)
          const { data } = result
          if (data) {
            this.notify(data)
            this.queryLoyalties()
            this.resetForm()
          }
        } catch (err) {
          console.log(err)
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
          this.submitting = false
        }
      } else {
        this.$snotify.error('Invalid API URL')
      }
    },
    resetForm() {
      this.loyaltyForm.groupId = undefined
      this.loyaltyForm.effDate = undefined
      this.loyaltyForm.expDate = undefined
      this.loyaltyForm.noPastTran = 0
      this.loyaltyForm.noPastDays = 0
      this.loyaltyForm.minTotPrem = 0.00
      this.addLoyaltyDialog = false
      this.newLoyalty = false
      this.selectedLoyalty = null
      this.deleteLoyalty = ''
      this.menu = []
    },
    formatDisplayNumber(xNum) {
      return formatDisplayNumber(xNum)
    }
  },
  created() {
    this.doInit()
  },
  mixins: [
    TableConst.default,
    MessageMixin
  ]
}
</script>

<style lang="scss">
</style>
